/**
 * @fileoverview Provides context sizing utilities specifically for PDF documents,
 * which are based on structured word data rather than character offsets.
 */
import { smartTruncate } from './sizing.js';
/**
 * Extracts a precise contextual window around a selection within a PDF document.
 * This function operates on word IDs and indices, not character offsets.
 * The intra-word context (the parts of the start/end words not selected)
 * must be pre-calculated and passed into this function.
 *
 * @param allParagraphs - A window of paragraphs, each containing word data.
 * @param selectedParagraphIndex - The index of the selected paragraph within `allParagraphs`.
 * @param startWordId - The ID of the first word in the selection.
 * @param endWordId - The ID of the last word in the selection.
 * @param intraWordPrefix - The text within the start-word that comes *before* the selection.
 * @param intraWordSuffix - The text within the end-word that comes *after* the selection.
 * @returns An object containing the text before (prefix) and after (suffix) the selection.
 */
export function getPreciseContextAroundPdfSelection(allParagraphs, selectedParagraphIndex, startWordId, endWordId, intraWordPrefix, intraWordSuffix) {
    if (!allParagraphs ||
        allParagraphs.length === 0 ||
        selectedParagraphIndex < 0 ||
        selectedParagraphIndex >= allParagraphs.length) {
        return { beforeText: '', afterText: '' };
    }
    const currentParagraph = allParagraphs[selectedParagraphIndex];
    if (!currentParagraph.words || currentParagraph.words.length === 0) {
        return { beforeText: '', afterText: '' };
    }
    // 1. Find the start and end indices of the selection within the current paragraph's words array.
    const startWordIndex = currentParagraph.words.findIndex(w => w.id === startWordId);
    const endWordIndex = currentParagraph.words.findIndex(w => w.id === endWordId);
    if (startWordIndex === -1 || endWordIndex === -1) {
        // If words are not found, we cannot accurately determine context.
        return { beforeText: '', afterText: '' };
    }
    // 2. Build the `beforeText`
    let beforeText = '';
    // a. Prepend content from all paragraphs before the selected one.
    for (let i = 0; i < selectedParagraphIndex; i++) {
        beforeText += allParagraphs[i].content + ' '; // Use full content for preceding paragraphs
    }
    // b. Append content from the current paragraph before the selection start word.
    const beforeWordsInPara = currentParagraph.words
        .slice(0, startWordIndex)
        .map(w => w.text)
        .join(' ');
    if (beforeWordsInPara) {
        beforeText += beforeWordsInPara;
    }
    // c. Append the pre-calculated intra-word prefix.
    if (intraWordPrefix) {
        if (beforeText.length > 0 && !beforeText.endsWith(' ')) {
            beforeText += ' ';
        }
        beforeText += intraWordPrefix;
    }
    // 3. Build the `afterText`
    let afterText = '';
    // a. Prepend the pre-calculated intra-word suffix.
    afterText += intraWordSuffix;
    // b. Append content from the current paragraph after the selection end word.
    const afterWordsInPara = currentParagraph.words
        .slice(endWordIndex + 1)
        .map(w => w.text)
        .join(' ');
    if (afterWordsInPara) {
        if (afterText.length > 0 && !afterText.endsWith(' ')) {
            afterText += ' ';
        }
        afterText += afterWordsInPara;
    }
    // c. Append content from all paragraphs after the selected one.
    for (let i = selectedParagraphIndex + 1; i < allParagraphs.length; i++) {
        if (afterText.length > 0 && !afterText.endsWith(' ')) {
            afterText += ' ';
        }
        afterText += allParagraphs[i].content;
    }
    // 4. Apply the same truncation logic to maintain consistency.
    // The limits (e.g., 50, 200) are placeholders and should be aligned with `sizing.ts`.
    const truncatedBeforeText = smartTruncate(beforeText.trim(), 50, 200, true);
    const truncatedAfterText = smartTruncate(afterText.trim(), 50, 200, false);
    return {
        beforeText: truncatedBeforeText,
        afterText: truncatedAfterText,
    };
}
//# sourceMappingURL=pdf-sizing.js.map